import React, { useEffect, useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Dimensions,
  Platform,
} from 'react-native';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { useMaintenanceStore } from '../../stores/maintenanceStore';
import { WorkshopTimeGraph } from '../../components/maintenance/WorkshopTimeGraph';
import { WorkTypePieChart } from '../../components/maintenance/WorkTypePieChart';
import { WorkTypeLineChart } from '../../components/maintenance/WorkTypeLineChart';
import { MakeModelBreakdown } from '../../components/maintenance/MakeModelBreakdown';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';
import { useNavigationContext } from '../../navigation/NavigationContext';

const { width } = Dimensions.get('window');

export const MaintenanceDashboardScreen = () => {
  const navigation = useNavigation<any>();
  const { setCurrentRoute } = useNavigationContext();
  const isWeb = Platform.OS === 'web';
  // Check if navigation is ready
  const isNavigationReady = useNavigationState(state => state !== undefined);
  const { jobCards, loading, error, fetchJobCards } = useMaintenanceStore();
  const [refreshing, setRefreshing] = useState(false);
  
  // Calculate current month and 3 months before dynamically
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-indexed
  const month3Before = currentMonth - 3 < 0 ? currentMonth - 3 + 12 : currentMonth - 3;
  const month2Before = currentMonth - 2 < 0 ? currentMonth - 2 + 12 : currentMonth - 2;
  const month1Before = currentMonth - 1 < 0 ? currentMonth - 1 + 12 : currentMonth - 1;
  
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);

  useEffect(() => {
    fetchJobCards();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchJobCards();
    setRefreshing(false);
  };

  // Calculate statistics
  const activeJobCards = jobCards.filter(jc => 
    jc.jobStatus === 'pending' || jc.jobStatus === 'in_progress'
  );

  const completedThisMonth = jobCards.filter(jc => {
    if (jc.jobStatus !== 'completed' && jc.jobStatus !== 'handed_over') return false;
    const returnDate = jc.returnDate ? new Date(jc.returnDate) : null;
    if (!returnDate) return false;
    const now = new Date();
    return returnDate.getMonth() === now.getMonth() && 
           returnDate.getFullYear() === now.getFullYear();
  });

  // Category breakdown
  const underMaintenance = activeJobCards.filter(jc => jc.underMaintenance).length;
  const accidentRepair = activeJobCards.filter(jc => jc.accidentRepair).length;
  const alQouz = activeJobCards.filter(jc => jc.underMaintenanceAlQouz).length;
  const inAgency = activeJobCards.filter(jc => jc.underMaintenanceAgency).length;
  const others = activeJobCards.filter(jc => jc.others).length;

  // Average workshop time
  const completedJobs = jobCards.filter(jc => 
    jc.jobStatus === 'completed' || jc.jobStatus === 'handed_over'
  );
  
  let avgWorkshopDays = 0;
  if (completedJobs.length > 0) {
    const totalDays = completedJobs.reduce((sum, jc) => {
      if (!jc.handoverDate || !jc.returnDate) return sum;
      const start = new Date(jc.handoverDate);
      const end = new Date(jc.returnDate);
      const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      return sum + days;
    }, 0);
    avgWorkshopDays = Math.round(totalDays / completedJobs.length);
  }

  // Pending delivery
  const pendingDelivery = jobCards.filter(jc => 
    jc.jobStatus === 'completed' && !jc.returnDate
  ).length;

  if (error) {
    return (
      <View style={styles.centered}>
        <Ionicons name="warning-outline" size={48} color={theme.colors.error} />
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity style={styles.refreshButton} onPress={handleRefresh}>
          <Text style={styles.refreshButtonText}>Try Again</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.headerTitle}>Workshop Dashboard</Text>
          <Text style={styles.headerSubtitle}>Real-time maintenance overview</Text>
        </View>
        <TouchableOpacity 
          style={styles.refreshButton}
          onPress={handleRefresh}
          disabled={refreshing}
        >
          <Ionicons 
            name="refresh" 
            size={24} 
            color={theme.colors.primary} 
            style={refreshing ? { transform: [{ rotate: '180deg' }] } : {}}
          />
        </TouchableOpacity>
      </View>

      {loading && !refreshing ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      ) : (
        <>
          {/* Key Metrics */}
          <View style={styles.metricsGrid}>
            <View style={[styles.metricCard, { backgroundColor: theme.colors.primary }]}>
              <Ionicons name="construct" size={32} color="#fff" />
              <Text style={styles.metricValue}>{activeJobCards.length}</Text>
              <Text style={styles.metricLabel}>In Workshop</Text>
            </View>

            <View style={[styles.metricCard, { backgroundColor: '#10b981' }]}>
              <Ionicons name="checkmark-circle" size={32} color="#fff" />
              <Text style={styles.metricValue}>{completedThisMonth.length}</Text>
              <Text style={styles.metricLabel}>Completed This Month</Text>
            </View>

            <View style={[styles.metricCard, { backgroundColor: '#f59e0b' }]}>
              <Ionicons name="time" size={32} color="#fff" />
              <Text style={styles.metricValue}>{avgWorkshopDays}</Text>
              <Text style={styles.metricLabel}>Avg. Days in Shop</Text>
            </View>

            <View style={[styles.metricCard, { backgroundColor: '#ef4444' }]}>
              <Ionicons name="alert-circle" size={32} color="#fff" />
              <Text style={styles.metricValue}>{pendingDelivery}</Text>
              <Text style={styles.metricLabel}>Pending Delivery</Text>
            </View>
          </View>

          {/* Workshop Breakdown */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Workshop Breakdown</Text>
            <View style={styles.breakdownContainer}>
              <View style={styles.breakdownRow}>
                <View style={styles.breakdownItem}>
                  <View style={[styles.breakdownDot, { backgroundColor: '#3b82f6' }]} />
                  <Text style={styles.breakdownLabel}>Under Maintenance</Text>
                  <Text style={styles.breakdownValue}>{underMaintenance}</Text>
                </View>
                <View style={styles.breakdownItem}>
                  <View style={[styles.breakdownDot, { backgroundColor: '#ef4444' }]} />
                  <Text style={styles.breakdownLabel}>Accident Repair</Text>
                  <Text style={styles.breakdownValue}>{accidentRepair}</Text>
                </View>
              </View>
              <View style={styles.breakdownRow}>
                <View style={styles.breakdownItem}>
                  <View style={[styles.breakdownDot, { backgroundColor: '#10b981' }]} />
                  <Text style={styles.breakdownLabel}>Al Qouz</Text>
                  <Text style={styles.breakdownValue}>{alQouz}</Text>
                </View>
                <View style={styles.breakdownItem}>
                  <View style={[styles.breakdownDot, { backgroundColor: '#f59e0b' }]} />
                  <Text style={styles.breakdownLabel}>In Agency</Text>
                  <Text style={styles.breakdownValue}>{inAgency}</Text>
                </View>
              </View>
              <View style={styles.breakdownRow}>
                <View style={styles.breakdownItem}>
                  <View style={[styles.breakdownDot, { backgroundColor: '#8b5cf6' }]} />
                  <Text style={styles.breakdownLabel}>Others</Text>
                  <Text style={styles.breakdownValue}>{others}</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Workshop Time Graph - Moved here */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Cars at Workshop Over Time</Text>
            <WorkshopTimeGraph jobCards={jobCards} />
          </View>

          {/* Pie Chart and Make/Model side by side */}
          <View style={styles.sideBySideSection}>
            <View style={styles.halfWidth}>
              <Text style={styles.sectionTitle}>Work Type Distribution</Text>
              <WorkTypePieChart
                underMaintenance={underMaintenance}
                accidentRepair={accidentRepair}
                alQouz={alQouz}
                agency={inAgency}
                others={others}
              />
            </View>
            <View style={styles.halfWidth}>
              <MakeModelBreakdown jobCards={jobCards} />
            </View>
          </View>

          {/* Work Type Trend Charts with Month Selector */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Work Type Trends</Text>
              <View style={styles.monthSelector}>
                <TouchableOpacity
                  style={[styles.monthButton, selectedMonth === month3Before && styles.monthButtonActive]}
                  onPress={() => setSelectedMonth(month3Before)}
                >
                  <Text style={[styles.monthButtonText, selectedMonth === month3Before && styles.monthButtonTextActive]}>
                    {new Date(now.getFullYear(), month3Before, 1).toLocaleDateString('en-US', { month: 'long' })}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.monthButton, selectedMonth === month2Before && styles.monthButtonActive]}
                  onPress={() => setSelectedMonth(month2Before)}
                >
                  <Text style={[styles.monthButtonText, selectedMonth === month2Before && styles.monthButtonTextActive]}>
                    {new Date(now.getFullYear(), month2Before, 1).toLocaleDateString('en-US', { month: 'long' })}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.monthButton, selectedMonth === month1Before && styles.monthButtonActive]}
                  onPress={() => setSelectedMonth(month1Before)}
                >
                  <Text style={[styles.monthButtonText, selectedMonth === month1Before && styles.monthButtonTextActive]}>
                    {new Date(now.getFullYear(), month1Before, 1).toLocaleDateString('en-US', { month: 'long' })}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.monthButton, selectedMonth === currentMonth && styles.monthButtonActive]}
                  onPress={() => setSelectedMonth(currentMonth)}
                >
                  <Text style={[styles.monthButtonText, selectedMonth === currentMonth && styles.monthButtonTextActive]}>
                    {new Date(now.getFullYear(), currentMonth, 1).toLocaleDateString('en-US', { month: 'long' })}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.monthButton, selectedMonth === -1 && styles.monthButtonActive]}
                  onPress={() => setSelectedMonth(-1)}
                >
                  <Text style={[styles.monthButtonText, selectedMonth === -1 && styles.monthButtonTextActive]}>Total</Text>
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.trendsGrid}>
              <View style={styles.trendItem}>
                <WorkTypeLineChart
                  jobCards={jobCards}
                  workType="maintenance"
                  title="Maintenance"
                  color="#3b82f6"
                  selectedMonth={selectedMonth}
                />
              </View>
              <View style={styles.trendItem}>
                <WorkTypeLineChart
                  jobCards={jobCards}
                  workType="accident"
                  title="Accident Repair"
                  color="#ef4444"
                  selectedMonth={selectedMonth}
                />
              </View>
              <View style={styles.trendItem}>
                <WorkTypeLineChart
                  jobCards={jobCards}
                  workType="alQouz"
                  title="Al Qouz"
                  color="#10b981"
                  selectedMonth={selectedMonth}
                />
              </View>
              <View style={styles.trendItem}>
                <WorkTypeLineChart
                  jobCards={jobCards}
                  workType="agency"
                  title="Agency"
                  color="#f59e0b"
                  selectedMonth={selectedMonth}
                />
              </View>
            </View>
          </View>

          {/* Quick Actions */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Quick Actions</Text>
            <View style={styles.actionsGrid}>
              <TouchableOpacity 
                style={styles.actionCard}
                onPress={() => {
                  if (isWeb) {
                    setCurrentRoute('MaintenanceJobCards');
                  } else if (isNavigationReady) {
                    navigation.navigate('MaintenanceJobCards');
                  }
                }}
              >
                <Ionicons name="clipboard" size={32} color={theme.colors.primary} />
                <Text style={styles.actionLabel}>View Job Cards</Text>
              </TouchableOpacity>

              <TouchableOpacity 
                style={styles.actionCard}
                onPress={() => {
                  if (isWeb) {
                    setCurrentRoute('AddJobCard');
                  } else if (isNavigationReady) {
                    navigation.navigate('AddJobCard');
                  }
                }}
              >
                <Ionicons name="add-circle" size={32} color="#10b981" />
                <Text style={styles.actionLabel}>New Job Card</Text>
              </TouchableOpacity>

              <TouchableOpacity 
                style={styles.actionCard}
                onPress={() => {
                  if (isWeb) {
                    setCurrentRoute('MaintenanceCreditReport');
                  } else if (isNavigationReady) {
                    navigation.navigate('MaintenanceCreditReport');
                  }
                }}
              >
                <Ionicons name="document-text" size={32} color="#f59e0b" />
                <Text style={styles.actionLabel}>Credit Report</Text>
              </TouchableOpacity>
            </View>
          </View>
        </>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  contentContainer: {
    padding: 16,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
  refreshButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: theme.colors.surface,
  },
  refreshButtonText: {
    color: theme.colors.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  errorText: {
    fontSize: 16,
    color: theme.colors.error,
    marginVertical: 16,
    textAlign: 'center',
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  metricCard: {
    flex: 1,
    minWidth: width < 768 ? '47%' : '23%',
    padding: 20,
    borderRadius: 12,
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  metricValue: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#fff',
    marginTop: 8,
  },
  metricLabel: {
    fontSize: 12,
    color: '#fff',
    opacity: 0.9,
    marginTop: 4,
    textAlign: 'center',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 16,
  },
  breakdownContainer: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    padding: 16,
  },
  breakdownRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  breakdownItem: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    backgroundColor: theme.colors.background,
    borderRadius: 8,
  },
  breakdownDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  breakdownLabel: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.textPrimary,
  },
  breakdownValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  actionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  actionCard: {
    flex: 1,
    minWidth: width < 768 ? '47%' : '30%',
    padding: 20,
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  actionLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginTop: 8,
    textAlign: 'center',
  },
  trendsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  trendItem: {
    flex: 1,
    minWidth: width < 768 ? '100%' : '48%',
  },
  sideBySideSection: {
    flexDirection: width < 1024 ? 'column' : 'row',
    gap: 16,
    marginBottom: 24,
  },
  halfWidth: {
    flex: 1,
  },
  sectionHeader: {
    flexDirection: width < 768 ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: width < 768 ? 'flex-start' : 'center',
    marginBottom: 16,
    gap: 12,
  },
  monthSelector: {
    flexDirection: 'row',
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    padding: 4,
    gap: 4,
  },
  monthButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
  },
  monthButtonActive: {
    backgroundColor: theme.colors.primary,
  },
  monthButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textSecondary,
  },
  monthButtonTextActive: {
    color: '#fff',
  },
});
